import React, {
  Suspense, lazy, useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Fade, Typography } from '@material-ui/core';
import { isEmpty, get, map } from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import { Grid, Snackbar, Spinner } from '../../common/components';
import * as actions from '../actions';
import {
  DescriptiveQuestion,
  MultipleSelectionQuestion,
  SingleSelectionQuestion,
} from '../components';
import messages from '../messages';

const FloatingPDFViewer = lazy(() => import(/* webpackChunkName: 'FloatingPDFViewer', webpackPrefetch: true */ '../components/FloatingPDFViewer'));

const S = {
  Paragraph: styled(Typography)`
    color: ${({ theme }) => theme.palette.grey[800]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  QuestionItem: styled.li`
    margin-bottom: 80px;
  `,
};

const Stats = ({
  fileUrl, match, newsletter, survey,
}) => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const stats = useSelector(state => state.newsletter.stats);
  const questionUpdate = useSelector(state => state.newsletter.questionUpdate);
  const individuals = useSelector(state => state.newsletter.individuals);

  const dispatch = useDispatch();

  const handleDrawComplete = useCallback(() => setVisible(true), []);
  const updateQuestionTitle = useCallback((...args) => dispatch(actions.updateQuestionTitleRequest(...args)), [dispatch]);

  const handleClose = () => setOpen(false);

  const handleEnter = () => {
    setErrorMessage('저장에 실패하였습니다.');
  };

  const handleExited = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    if (questionUpdate.error) {
      setOpen(true);

      dispatch(actions.initQuestionUpdate());
    }
  }, [dispatch, questionUpdate.error]);

  useEffect(() => {
    dispatch(actions.fetchStatsRequest({ nid: match.params.nid, sid: match.params.sid }));
  }, [dispatch, match.params.nid, match.params.sid]);

  useEffect(() => {
    dispatch(actions.fetchIndividualsRequest({ nid: match.params.nid, sid: match.params.sid }));
  }, [dispatch, match.params.nid, match.params.sid]);

  return (
    <>
      <Grid item xs={2} />
      <Grid item xs={4}>
        {fileUrl && (
          <Suspense fallback={<Spinner />}>
            <FloatingPDFViewer fileUrl={fileUrl} survey={survey} onDrawComplete={handleDrawComplete} />
          </Suspense>
        )}
      </Grid>
      {visible && (
        <Grid item xs={6}>
          {!isEmpty(get('questions', survey.data))
            ? (
              <>
                {get('recordCount', newsletter.data)
                  ? (
                    <ul>
                      {map(question => (
                        <S.QuestionItem key={question.number}>
                          {{
                            SINGLE_SELECTION: question.type === 'SINGLE_SELECTION' && get(question.number, stats.data) && (
                              <SingleSelectionQuestion
                                question={question}
                                stats={get(question.number, stats.data)}
                                onUpdate={updateQuestionTitle}
                              />
                            ),
                            MULTIPLE_SELECTION: question.type === 'MULTIPLE_SELECTION' && get(question.number, stats.data) && (
                              <MultipleSelectionQuestion
                                question={question}
                                stats={get(question.number, stats.data)}
                                individuals={individuals}
                                onUpdate={updateQuestionTitle}
                              />
                            ),
                            DESCRIPTIVE: question.type === 'DESCRIPTIVE' && get(question.number, stats.data) && (
                              <DescriptiveQuestion
                                question={question}
                                stats={get(question.number, stats.data)}
                                onUpdate={updateQuestionTitle}
                              />
                            ),
                            // XXX: 하위 호환성 유지를 위한 필드. 추후 제거 예정
                            SINGLE_CHOICE: question.type === 'SINGLE_CHOICE' && get(question.number, stats.data) && (
                              <SingleSelectionQuestion
                                question={question}
                                stats={get(question.number, stats.data)}
                                onUpdate={updateQuestionTitle}
                              />
                            ),
                            MULTIPLE_CHOICE: question.type === 'MULTIPLE_CHOICE' && get(question.number, stats.data) && (
                              <MultipleSelectionQuestion
                                question={question}
                                stats={get(question.number, stats.data)}
                                onUpdate={updateQuestionTitle}
                              />
                            ),
                            SUBJECIVE: question.type === 'SUBJECTIVE' && get(question.number, stats.data) && (
                              <DescriptiveQuestion
                                question={question}
                                stats={get(question.number, stats.data)}
                                onUpdate={updateQuestionTitle}
                              />
                            ),
                          }[question.type] || null}
                        </S.QuestionItem>
                      ), get('questions', survey.data))}
                    </ul>
                  )
                  : (
                    <S.Paragraph>
                      <FormattedMessage {...messages.statsParagraphNoResponse} />
                    </S.Paragraph>
                  )}
              </>
            )
            : (
              <S.Paragraph>
                <FormattedMessage {...messages.statsParagraphNoQuestion} />
              </S.Paragraph>
            )}
        </Grid>
      )}
      <Snackbar
        autoHideDuration={3500}
        open={open}
        onClose={handleClose}
        onEnter={handleEnter}
        onExited={handleExited}
        message={errorMessage}
        TransitionComponent={Fade}
      />
    </>
  );
};

Stats.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      nid: PropTypes.string.isRequired,
      sid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  newsletter: PropTypes.shape({
    data: PropTypes.shape({
      recordCount: PropTypes.number.isRequired,
    }),
  }).isRequired,
  survey: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
};

export default Stats;
